/*
 * Recent change: Honors post-save level access before offering note-derived task actions.
 * AccountNotes.tsx
 *
 * Features:
 * - Responsive account notes workspace with desktop three-panel and compact swipe navigation
 * - Contact and note search with filtered views and virtualization
 * - Note creation, editing, and deletion scoped to account/contact context
 *
 * Logic:
 * - Loads account contacts and notes in parallel and derives contact note counts
 * - Keeps one live panel tree and switches compact panels with local state and touch gestures
 * - Uses selected contact context to create correctly scoped notes
 *
 * Changes:
 * - Added compact Contacts/Notes/Details navigation with swipe support below desktop
 * - Adjusted panel sizing and header layouts to prevent narrow-screen overflow
 * - Preserved desktop three-column behavior and existing account-scoped actions
 * - Added an `Add Contact` button beside the contacts search input
 * - Integrated the NewContact modal in account-scoped mode so new contacts are created for the current account
 * - Added compact Contacts/Notes/Details navigation with swipe support below desktop
 * - Replaced local task drafts with persisted task creation and lookup for note-linked work
 * - Notes can now be deleted only when they have no active linked tasks
 * - Aligns the note-linked task dialog chrome with the shared New Task modal
 * - Sends note-linked assignment-email opt-in with task creation and displays backend queue outcomes
 * - Removes redundant toolbar and note-card task creation shortcuts while retaining editor-based task creation
 * - Defaults note-linked task assignment to the authenticated task creator
 * - Persists the priority selected in the note-linked task dialog
 * - Normalizes missing contact IDs so account-note counts and card filters stay aligned
 * - Uses the shared wide responsive shell for note-linked task creation.
 * - Applies the shared note design to the responsive account/contact details editor.
 */

import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useEventBus } from '../../context/EventBusContext';
import { toast } from 'react-toastify';
import { getNotesSummaryPrompt } from '../../utils/summaryUtils';
import { Contact } from '../../models/Contact';
import { Note, NOTE_FLAGS, createBlankNote } from '../../models/Note';
import NoteEditor from './NoteEditor';
import NoteCard from './NoteCard';
import NoteTaskPrototypePanel, { type NoteTaskContextEntity, type NoteTaskFormValues } from './NoteTaskPrototypePanel';
import {
  TASK_POPUP_CLOSE_CLASS_NAME,
  TASK_POPUP_HEADER_CLASS_NAME,
  TASK_POPUP_TITLE_CLASS_NAME,
} from '../task/popup';
import { request } from '../../services/dataService';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { EVENTS, type NotesUpdatedEventData } from '../../constants/eventConstants';
import { createTask, getTasks, getTasksForNote, TASKS_UPDATED_EVENT } from '../../services/taskService';
import { showTaskNotificationOutcome } from '../task/taskNotificationToast';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { BREAKPOINTS } from '../../constants/layoutConstants';
import { useTeams } from '../../context/TeamContext';
import { SchemaProvider } from '../../context/SchemaContext';
import VirtualizedList, { ListItem } from '../common/VirtualizedList';
import NewContact from '../contact/NewContact';
import { DialogShell } from '../primitives';
import type { TaskMutationPayload } from '../../models/Task';

interface ContactNotesProps {
  accountId: number;
  accountName: string;
  initialContactId?: number;
  initialNoteId?: number;
  /**
   * Used to pre-select and lock the team selection when creating new notes.
   * When provided, only this team will be available for selection.
   * When not provided, all teams will be available for selection.
   */
  accountTeamId?: number | null;
  rlpCompanyId?: number | null;
}

type ContactWithCount = Contact & {
  noteCount: number;
};

type CompactPanel = 'contacts' | 'notes' | 'details';

const COMPACT_PANEL_ORDER: CompactPanel[] = ['contacts', 'notes', 'details'];
const COMPACT_PANEL_INDEX: Record<CompactPanel, number> = {
  contacts: 0,
  notes: 1,
  details: 2,
};
const COMPACT_PANEL_LABELS: Record<CompactPanel, string> = {
  contacts: 'Contacts',
  notes: 'Notes',
  details: 'Details',
};
const COMPACT_SWIPE_THRESHOLD = 48;

const getNoteContactId = (note: Note): number => note.contactId ?? 0;

const ContactNotes: FC<ContactNotesProps> = ({
  accountId,
  accountName,
  initialContactId,
  initialNoteId,
  accountTeamId,
  rlpCompanyId
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<(ListItem | ContactWithCount)[]>([]);
  const { currentUserId } = useTeams();
  const { publish, subscribe, unsubscribe } = useEventBus();
  const [allNotes, setAllNotes] = useState<Note[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<number>(initialContactId ?? -1);
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(initialNoteId ?? null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [isNewContactOpen, setIsNewContactOpen] = useState(false);
  const [taskPromptNote, setTaskPromptNote] = useState<Note | null>(null);
  const [taskPromptContext, setTaskPromptContext] = useState<NoteTaskContextEntity | null>(null);
  const [noteTaskCounts, setNoteTaskCounts] = useState<Map<number, number>>(new Map());
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    return window.matchMedia(BREAKPOINTS.desktop).matches;
  });
  const [activeCompactPanel, setActiveCompactPanel] = useState<CompactPanel>('notes');
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchCurrentRef = useRef<{ x: number; y: number } | null>(null);
  const touchTargetBlockedRef = useRef(false);
  const navigate = useNavigate();
  const isCompactLayout = !isDesktopLayout;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(BREAKPOINTS.desktop);
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktopLayout(event.matches);
    };

    setIsDesktopLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  useEffect(() => {
    setActiveCompactPanel('notes');
  }, [accountId]);

  // Move fetchData outside useEffect
  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [contacts, notes] = await Promise.all([
        request(API_ENDPOINTS.GET_ACCOUNT_CONTACTS, { id: accountId, initialContactId }),
        request(API_ENDPOINTS.GET_ACCOUNT_NOTES, { id: accountId })
      ]);

      // Calculate note counts
      const noteCounts = new Map<number, number>();
      notes.forEach((note: Note) => {
        const contactId = getNoteContactId(note);
        noteCounts.set(contactId, (noteCounts.get(contactId) || 0) + 1);
      });

      // Add account level pseudo-contacts at the top
      const allNotesItem: ListItem = {
        id: -1,
        displayName: '(All notes)',
        isAccountLevel: true,
        noteCount: notes.length
      };

      const accountNotesItem: ListItem = {
        id: 0,
        displayName: '(Account notes)',
        isAccountLevel: true,
        noteCount: noteCounts.get(0) || 0
      };

      // Add note counts to contacts
      const contactsWithCounts: ContactWithCount[] = contacts.map((contact: Contact) => ({
        ...contact,
        noteCount: noteCounts.get(contact.id) || 0
      }));

      setData([allNotesItem, accountNotesItem, ...contactsWithCounts]);
      // Sort notes by updatedAt before setting them
      const sortedNotes = notes.sort((a: Note, b: Note) =>
        (new Date(b.updatedAt ?? b.createdAt).getTime()) -
        (new Date(a.updatedAt ?? a.createdAt).getTime())
      );
      setAllNotes(sortedNotes);

      // Only set selectedNoteId if we have an initialNoteId and that note exists
      if (initialNoteId && sortedNotes.find((note: Note) => note.id === initialNoteId)) {
        setSelectedNoteId(initialNoteId);
      } else {
        setSelectedNoteId((currentSelectedNoteId) => (
          currentSelectedNoteId && !sortedNotes.find((note: Note) => note.id === currentSelectedNoteId)
            ? null
            : currentSelectedNoteId
        ));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId, initialContactId, initialNoteId, accountName]);

  // Use fetchData in useEffect
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshNoteTaskCounts = React.useCallback(async () => {
    const noteIds = Array.from(new Set(allNotes.map((note) => note.id).filter((noteId) => noteId > 0)));
    if (!noteIds.length) {
      setNoteTaskCounts(new Map());
      return;
    }

    try {
      const noteIdSet = new Set(noteIds);
      const accountTasks = await getTasks({ accountId });
      const nextTaskCounts = new Map<number, number>();

      accountTasks.forEach((task) => {
        if (task.sourceNoteId && noteIdSet.has(task.sourceNoteId)) {
          nextTaskCounts.set(task.sourceNoteId, (nextTaskCounts.get(task.sourceNoteId) ?? 0) + 1);
        }
      });

      setNoteTaskCounts(nextTaskCounts);
    } catch (error) {
      console.error('Failed to load note task counts:', error);
    }
  }, [accountId, allNotes]);

  useEffect(() => {
    void refreshNoteTaskCounts();
  }, [refreshNoteTaskCounts]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleTasksUpdated = () => {
      void refreshNoteTaskCounts();
    };

    window.addEventListener(TASKS_UPDATED_EVENT, handleTasksUpdated);
    return () => window.removeEventListener(TASKS_UPDATED_EVENT, handleTasksUpdated);
  }, [refreshNoteTaskCounts]);

  React.useEffect(() => {
    const handleNotesUpdated = (data?: NotesUpdatedEventData) => {
      if (data?.accountId && data.accountId !== accountId) {
        return;
      }

      void fetchData();
    };

    subscribe(EVENTS.NOTES_UPDATED, handleNotesUpdated);

    return () => {
      unsubscribe(EVENTS.NOTES_UPDATED, handleNotesUpdated);
    };
  }, [accountId, fetchData, subscribe, unsubscribe]);

  // Add effect to handle initialNoteId
  useEffect(() => {
    if (initialNoteId) {
      const note = allNotes.find(note => note.id === initialNoteId);
      if (note) {
        setSelectedNoteId(initialNoteId);
        setIsEditing(false);
      }
    }
  }, [initialNoteId, allNotes]);

  // Filter notes based on selected contact and search query
  const filteredNotes = allNotes.filter((note: Note) => {
    const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 0);
    const matchesSearch = searchTerms.length === 0 || searchTerms.every(term =>
      (note.content?.toLowerCase().includes(term) ?? false) ||
      (note.author?.toLowerCase().includes(term) ?? false)
    );
    const matchesContact = selectedContactId === -1 ||
      getNoteContactId(note) === selectedContactId;
    return matchesSearch && matchesContact;
  });

  // Filter contacts based on search query
  const filteredData = data.filter(item => {
    if (item.isAccountLevel) return true; // Always show account level items

    // Split search query into terms and check if all terms are included
    const searchTerms = contactSearchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 0);
    if (searchTerms.length === 0) return true;

    const displayName = (item.displayName || '').toLowerCase();
    return searchTerms.every(term => displayName.includes(term));
  });

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedNoteId(null);

    if (isCompactLayout) {
      setActiveCompactPanel('notes');
    }
  };

  const buildSavedNoteSnapshot = (note: Note, noteId: number): Note => {
    const selectedContact = note.contactId ? data.find((item) => item.id === note.contactId && !item.isAccountLevel) : undefined;

    return {
      ...note,
      id: noteId,
      accountName,
      contactName: note.contactId ? selectedContact?.displayName ?? note.contactName : undefined,
    };
  };

  const resolveCreatedNoteId = (noteResponse: { id?: number; noteId?: number } | Array<{ id?: number; noteId?: number }>) => {
    const normalizedResponse = Array.isArray(noteResponse) ? noteResponse[0] : noteResponse;
    return normalizedResponse?.id ?? normalizedResponse?.noteId ?? 0;
  };

  const buildTaskPromptContext = (note: Note): NoteTaskContextEntity => {
    if (note.contactId && note.contactId > 0) {
      const selectedContact = data.find((item) => item.id === note.contactId && !item.isAccountLevel);

      return {
        id: note.contactId,
        personId: 0,
        name: selectedContact?.displayName || note.contactName || `Contact #${note.contactId}`,
        type: 'Contact'
      };
    }

    return {
      id: accountId,
      personId: 0,
      name: accountName,
      type: 'Account'
    };
  };

  const openTaskPromptForNote = (note: Note) => {
    setTaskPromptNote(note);
    setTaskPromptContext(buildTaskPromptContext(note));
  };

  const handleCloseTaskPrompt = () => {
    setTaskPromptNote(null);
    setTaskPromptContext(null);
  };

  const handleSkipTaskPrompt = async () => {
    if (!taskPromptNote) {
      handleCloseTaskPrompt();
      return;
    }

    if (!NOTE_FLAGS.hasFlag(taskPromptNote, NOTE_FLAGS.TASKS)) {
      handleCloseTaskPrompt();
      return;
    }

    const noteWithoutTaskFlag = NOTE_FLAGS.clearFlag(taskPromptNote, NOTE_FLAGS.TASKS);

    try {
      const { id, ...noteDataWithoutId } = noteWithoutTaskFlag;
      await request(API_ENDPOINTS.PUT_NOTE, { id, ...noteDataWithoutId });
      await fetchData();
      toast.info('Tasks unchecked for this note.');
      handleCloseTaskPrompt();
    } catch (error) {
      console.error('Failed to clear Tasks for note:', error);
      toast.error('Failed to update the note.');
    }
  };

  const handleSaveTaskPrompt = async (taskValues: NoteTaskFormValues) => {
    if (!taskPromptNote) {
      return;
    }

    const taskPayload: TaskMutationPayload = {
      title: taskValues.title,
      description: taskValues.description.trim(),
      accountId,
      contactId: taskPromptNote.contactId && taskPromptNote.contactId > 0 ? taskPromptNote.contactId : null,
      sourceNoteId: taskPromptNote.id,
      dueDate: taskValues.dueDate || null,
      priority: taskValues.priority,
      sourceType: 'Note',
      assigneeIds: taskValues.assigneeIds,
      sendEmail: taskValues.sendEmail,
    };

    const creationResult = await createTask(taskPayload);
    const savedTask = creationResult.task;
    showTaskNotificationOutcome(creationResult.notification);

    toast.success(`Task "${savedTask.title}" created.`);
    setNoteTaskCounts((previousCounts) => {
      const nextCounts = new Map(previousCounts);
      nextCounts.set(taskPromptNote.id, (nextCounts.get(taskPromptNote.id) ?? 0) + 1);
      return nextCounts;
    });
    handleCloseTaskPrompt();
  };

  const handleSaveNote = async (updatedNote: Note, saveContext?: { canReadAfterSave: boolean }) => {
    try {
      let savedNoteId = updatedNote.id;

      if (!updatedNote.id) {
        // Creating a new note
        const newNoteResponse: (Note & { noteId?: number }) | Array<Note & { noteId?: number }> = await request(API_ENDPOINTS.POST_NOTE, updatedNote);
        const createdNoteId = resolveCreatedNoteId(newNoteResponse);

        if (!createdNoteId) {
          throw new Error('Created note response did not include a note ID');
        }

        setSelectedNoteId(createdNoteId);
        savedNoteId = createdNoteId;
      } else {
        // Updating an existing note
        const { id, ...noteDataWithoutId } = updatedNote;
        await request(API_ENDPOINTS.PUT_NOTE, { id, ...noteDataWithoutId });
      }

      const savedNoteSnapshot = buildSavedNoteSnapshot(updatedNote, savedNoteId);
      const shouldPromptForTask = saveContext?.canReadAfterSave !== false && NOTE_FLAGS.hasFlag(savedNoteSnapshot, NOTE_FLAGS.TASKS);

      setIsEditing(false);
      // Refresh data after successful save
      const fetchComplete = fetchData();
      toast.success(`Note ${updatedNote.id ? 'updated' : 'created'} successfully`);
      await fetchComplete;

      if (shouldPromptForTask) {
        openTaskPromptForNote(savedNoteSnapshot);
      }

      if (isCompactLayout) {
        setActiveCompactPanel('notes');
      }
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error('Failed to save note');
    }
  };

  const selectedNote = allNotes.find((note: Note) => note.id === selectedNoteId);

  // Get search terms for highlighting
  const searchTerms = contactSearchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 0);
  const selectedContactName = selectedContactId > 0
    ? data.find(item => item.id === selectedContactId)?.displayName
    : undefined;
  const detailsPlaceholderMessage = filteredNotes.length === 0
    ? 'No notes found for this selection'
    : 'Select a note to view details';

  const goToCompactPanel = (panel: CompactPanel) => {
    setActiveCompactPanel(panel);
  };

  const activeCompactPanelIndex = COMPACT_PANEL_INDEX[activeCompactPanel];
  const canMoveCompactPanelLeft = activeCompactPanelIndex > 0;
  const canMoveCompactPanelRight = activeCompactPanelIndex < COMPACT_PANEL_ORDER.length - 1;

  const moveCompactPanel = (direction: 'left' | 'right') => {
    setActiveCompactPanel((currentPanel) => {
      const currentIndex = COMPACT_PANEL_INDEX[currentPanel];
      const nextIndex = direction === 'left'
        ? Math.min(currentIndex + 1, COMPACT_PANEL_ORDER.length - 1)
        : Math.max(currentIndex - 1, 0);

      return COMPACT_PANEL_ORDER[nextIndex];
    });
  };

  const shouldIgnoreSwipeTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return Boolean(target.closest('input, textarea, select, button, a'));
  };

  const clearTouchTracking = () => {
    touchStartRef.current = null;
    touchCurrentRef.current = null;
    touchTargetBlockedRef.current = false;
  };

  const handleWorkspaceTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDesktopLayout) {
      return;
    }

    touchTargetBlockedRef.current = shouldIgnoreSwipeTarget(event.target);
    if (touchTargetBlockedRef.current) {
      return;
    }

    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleWorkspaceTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDesktopLayout || touchTargetBlockedRef.current || !touchStartRef.current) {
      return;
    }

    const touch = event.touches[0];
    touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleWorkspaceTouchEnd = () => {
    if (isDesktopLayout || touchTargetBlockedRef.current || !touchStartRef.current || !touchCurrentRef.current) {
      clearTouchTracking();
      return;
    }

    const deltaX = touchCurrentRef.current.x - touchStartRef.current.x;
    const deltaY = touchCurrentRef.current.y - touchStartRef.current.y;

    clearTouchTracking();

    if (Math.abs(deltaX) < COMPACT_SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    if (deltaX > 0) {
      moveCompactPanel('right');
      return;
    }

    moveCompactPanel('left');
  };

  const handleSelectContact = (id: number) => {
    setSelectedContactId(id);
    setSelectedNoteId(null);
    setIsEditing(false);

    if (isCompactLayout) {
      setActiveCompactPanel('notes');
    }
  };

  const handleSelectNote = (note: Note) => {
    setSelectedNoteId(note.id);

    // When in All notes view and selecting a note, update the contact selection
    if (selectedContactId === -1) {
      setSelectedContactId(note.contactId || 0);
    }

    setIsEditing(true);

    if (isCompactLayout) {
      setActiveCompactPanel('details');
    }
  };

  const getBlankNote = () => {
    const objectType = selectedContactId > 0 ? 'contact' : 'account';
    const blankNote = createBlankNote(objectType);

    blankNote.objectIds = selectedContactId > 0 ? selectedContactId.toString() : accountId.toString();
    blankNote.contactId = selectedContactId > 0 ? selectedContactId : 0;
    blankNote.createdBy = currentUserId || 0;
    blankNote.updatedBy = currentUserId || 0;

    return blankNote;
  };

  const handleNewNote = () => {
    setSelectedNoteId(null);
    // When clicking New Note in Account notes or contact view,
    // create note for that context. In All notes view, default to account note.
    if (selectedContactId === -1) {
      setSelectedContactId(0); // Default to account note in All notes view
    }
    setIsEditing(true);

    if (isCompactLayout) {
      setActiveCompactPanel('details');
    }
  };

  const compactTrackStyle = isCompactLayout
    ? {
      width: `${COMPACT_PANEL_ORDER.length * 100}%`,
      transform: `translateX(-${COMPACT_PANEL_INDEX[activeCompactPanel] * (100 / COMPACT_PANEL_ORDER.length)}%)`,
      touchAction: 'pan-y',
    }
    : undefined;

  const compactPanelStyle = isCompactLayout
    ? {
      width: `${100 / COMPACT_PANEL_ORDER.length}%`,
    }
    : undefined;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-3 py-2 dark:border-gray-700 lg:hidden">
        <button
          type="button"
          onClick={() => canMoveCompactPanelLeft && goToCompactPanel(COMPACT_PANEL_ORDER[activeCompactPanelIndex - 1])}
          disabled={!canMoveCompactPanelLeft}
          className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm transition-colors ${canMoveCompactPanelLeft
            ? `border-gray-300 ${THEME_CLASSES.text.primary} ${THEME_CLASSES.hover.background} dark:border-gray-600`
            : 'cursor-not-allowed border-gray-200 text-gray-300 dark:border-gray-700 dark:text-gray-600'}`}
          aria-label="Show previous panel"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
            <path fillRule="evenodd" d="M11.78 4.22a.75.75 0 010 1.06L7.06 10l4.72 4.72a.75.75 0 11-1.06 1.06l-5.25-5.25a.75.75 0 010-1.06l5.25-5.25a.75.75 0 011.06 0z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="flex min-w-0 flex-1 flex-col items-center justify-center text-center">
          <div className={`text-sm font-semibold ${THEME_CLASSES.text.primary}`}>
            {COMPACT_PANEL_LABELS[activeCompactPanel]}
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            {COMPACT_PANEL_ORDER.map((panel) => (
              <button
                key={panel}
                type="button"
                onClick={() => goToCompactPanel(panel)}
                className={`h-2.5 w-2.5 rounded-full transition-all ${activeCompactPanel === panel
                  ? 'bg-blue-600'
                  : 'bg-gray-300 dark:bg-gray-600'}`}
                aria-label={`Show ${COMPACT_PANEL_LABELS[panel]} panel`}
                aria-pressed={activeCompactPanel === panel}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => canMoveCompactPanelRight && goToCompactPanel(COMPACT_PANEL_ORDER[activeCompactPanelIndex + 1])}
          disabled={!canMoveCompactPanelRight}
          className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm transition-colors ${canMoveCompactPanelRight
            ? `border-gray-300 ${THEME_CLASSES.text.primary} ${THEME_CLASSES.hover.background} dark:border-gray-600`
            : 'cursor-not-allowed border-gray-200 text-gray-300 dark:border-gray-700 dark:text-gray-600'}`}
          aria-label="Show next panel"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
            <path fillRule="evenodd" d="M8.22 4.22a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L12.94 10 8.22 5.28a.75.75 0 010-1.06z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      <div className="sr-only lg:hidden" aria-live="polite">
        {COMPACT_PANEL_LABELS[activeCompactPanel]} panel selected
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <div
          className="flex h-full min-h-0 transition-transform duration-300 ease-out lg:transition-none"
          onTouchStart={handleWorkspaceTouchStart}
          onTouchMove={handleWorkspaceTouchMove}
          onTouchEnd={handleWorkspaceTouchEnd}
          onTouchCancel={clearTouchTracking}
          style={compactTrackStyle}
        >
          {/* Left Column - Contacts */}
          <div className="flex h-full min-h-0 w-full shrink-0 flex-col lg:w-1/4 lg:border-r lg:border-gray-200 lg:dark:border-gray-700" style={compactPanelStyle}>
            <div className="min-h-[4rem] shrink-0 border-b border-gray-200 p-4 dark:border-gray-700 lg:h-[4rem]">
              <div className="flex w-full items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <div className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                      <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={contactSearchQuery}
                    onChange={(e) => setContactSearchQuery(e.target.value)}
                    className="h-8 w-full rounded-md border pl-8 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                  />
                  {contactSearchQuery && (
                    <button
                      onClick={() => setContactSearchQuery('')}
                      className="absolute inset-y-0 right-2 flex items-center hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                      </svg>
                    </button>
                  )}
                </div>
                {!rlpCompanyId && (
                  <button
                    onClick={() => setIsNewContactOpen(true)}
                    className="h-8 whitespace-nowrap rounded-md bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700 sm:w-auto"
                  >
                    Add Contact
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              {loading ? (
                <div className="flex min-h-full items-center justify-center">
                  <div className={`animate-pulse ${THEME_CLASSES.text.secondary}`}>Loading...</div>
                </div>
              ) : filteredData.length === 0 ? (
                <div className={`px-4 py-3 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
                  No contacts found
                </div>
              ) : (
                <VirtualizedList
                  items={filteredData.map(item => ({
                    id: item.id,
                    displayName: item.displayName,
                    noteCount: item.noteCount,
                    isSpecialItem: item.isAccountLevel,
                    icon: item.id === -1 ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                        <path d="M5.566 4.657A4.505 4.505 0 016.75 4.5h10.5c.41 0 .806.055 1.183.157A3 3 0 0015.75 3h-7.5a3 3 0 00-2.684 1.657zM2.25 12a3 3 0 013-3h13.5a3 3 0 013 3v6a3 3 0 01-3 3H5.25a3 3 0 01-3-3v-6zM5.25 7.5c-.41 0-.806.055-1.184.157A3 3 0 016.75 6h10.5a3 3 0 012.684 1.657A4.505 4.505 0 0018.75 7.5H5.25z" />
                      </svg>
                    ) : item.id === 0 ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                        <path fillRule="evenodd" d="M4.125 3C3.089 3 2.25 3.84 2.25 4.875V18a3 3 0 003 3h15a3 3 0 01-3-3V4.875C17.25 3.839 16.41 3 15.375 3H4.125zM12 9.75a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5H12zm-.75-2.25a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5H12a.75.75 0 01-.75-.75zM6 12.75a.75.75 0 000 1.5h7.5a.75.75 0 000-1.5H6zm-.75 3.75a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5H6a.75.75 0 01-.75-.75zM6 6.75a.75.75 0 00-.75.75v3c0 .414.336.75.75.75h3a.75.75 0 00.75-.75v-3A.75.75 0 009 6.75H6z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                        <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                      </svg>
                    ),
                    onClick: !item.isAccountLevel ? () => navigate(`/contacts/${item.id}`) : undefined
                  }))}
                  selectedId={selectedContactId}
                  onSelect={handleSelectContact}
                  searchQuery={searchTerms}
                />
              )}
            </div>
          </div>

          {/* Middle Column - Notes List */}
          <div className="flex h-full min-h-0 w-full shrink-0 flex-col lg:w-[35%] lg:border-r lg:border-gray-200 lg:dark:border-gray-700" style={compactPanelStyle}>
            <div className="min-h-[4rem] shrink-0 border-b border-gray-200 p-4 dark:border-gray-700 lg:h-[4rem]">
              <div className="flex w-full items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <div className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                      <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 w-full rounded-md border pl-8 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-2 flex items-center hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={handleNewNote}
                    className="h-8 rounded-md bg-blue-600 px-3 text-xs font-medium whitespace-nowrap text-white hover:bg-blue-700"
                  >
                    New Note
                  </button>
                  <button
                    onClick={() => {
                      if (filteredNotes.length === 0) return;
                      const prompt = `Summarize only these ${filteredNotes.length} filtered notes.\n\n${getNotesSummaryPrompt(filteredNotes)}`;
                      publish('chat:open', prompt);
                    }}
                    className={`rounded-md p-2 ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
                    title="Ask AI about notes"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              {filteredNotes.map(note => (
                <NoteCard
                  key={`note-${note.id}`}
                  note={note}
                  isSelected={selectedNoteId === note.id}
                  onClick={() => handleSelectNote(note)}
                  searchQuery={searchQuery}
                  hasLinkedTasks={(noteTaskCounts.get(note.id) ?? 0) > 0}
                  onDeleteClick={async (noteToDelete) => {
                    try {
                      const linkedTasks = await getTasksForNote(noteToDelete.id);
                      if (linkedTasks.length > 0) {
                        toast.error(`This note cannot be deleted because it has ${linkedTasks.length} linked task${linkedTasks.length === 1 ? '' : 's'}. Please delete the linked task${linkedTasks.length === 1 ? '' : 's'} from the Tasks page first.`);
                        return;
                      }

                      if (!window.confirm('Are you sure you want to delete this note?')) {
                        return;
                      }

                      await request(API_ENDPOINTS.DELETE_NOTE, { id: noteToDelete.id });
                      await fetchData();
                      toast.success('Note deleted successfully');

                      if (selectedNoteId === noteToDelete.id) {
                        setSelectedNoteId(null);
                        setIsEditing(false);

                        if (isCompactLayout) {
                          setActiveCompactPanel('notes');
                        }
                      }
                    } catch (error) {
                      console.error('Error deleting note:', error);
                      toast.error('Failed to delete note');
                    }
                  }}
                />
              ))}
            </div>
          </div>

          {/* Right Column - Note Details */}
          <div className="flex h-full min-h-0 w-full shrink-0 flex-col overflow-hidden lg:w-[40%]" style={compactPanelStyle}>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {isEditing ? (
                <NoteEditor
                  note={selectedNote ?? getBlankNote()}
                  submitButtonText={selectedNoteId ? 'Save Changes' : 'Create Note'}
                  onSave={handleSaveNote}
                  onCancel={handleCancel}
                  onCreateTaskFromNote={(noteForTask) => openTaskPromptForNote(buildSavedNoteSnapshot(noteForTask, noteForTask.id))}
                  currentUserId={currentUserId || undefined}
                  accountName={selectedContactId === 0 ? accountName : undefined}
                  contactName={selectedContactName}
                  entityTeamId={accountTeamId || undefined}
                  layout="workspace-panel"
                />
              ) : (
                <div className={`flex h-full items-center justify-center p-6 text-center text-xs ${THEME_CLASSES.text.secondary}`}>
                  {detailsPlaceholderMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SchemaProvider>
        <NewContact
          isOpen={isNewContactOpen}
          onClose={() => setIsNewContactOpen(false)}
          initialAccount={{
            id: accountId,
            name: accountName,
            teamId: accountTeamId ?? null,
            city: '',
            province: '',
            rlpCompanyId: null
          }}
          lockInitialAccount
          onCreated={fetchData}
        />
      </SchemaProvider>

      <DialogShell
        open={Boolean(taskPromptNote)}
        title="New task"
        onClose={handleCloseTaskPrompt}
        panelClassName="!w-[calc(100vw-1.5rem)] !max-w-5xl !rounded-2xl sm:!w-[calc(100vw-3rem)]"
        headerClassName={TASK_POPUP_HEADER_CLASS_NAME}
        titleClassName={TASK_POPUP_TITLE_CLASS_NAME}
        closeButtonClassName={TASK_POPUP_CLOSE_CLASS_NAME}
        bodyPadding={false}
      >
        {taskPromptNote && (
          <NoteTaskPrototypePanel
            savedNote={taskPromptNote}
            selectedEntity={taskPromptContext}
            currentUserId={currentUserId}
            onClose={handleCloseTaskPrompt}
            onSkipTask={handleSkipTaskPrompt}
            onSubmitTask={handleSaveTaskPrompt}
            submitLabel="Save Task"
          />
        )}
      </DialogShell>
    </div>
  );
};

export default ContactNotes;
